import { useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { formatBytes } from '../../../hooks/useBackups'
import { useEnterpriseBundle, useUploadEnterprise, useRemoveEnterprise } from '../../../hooks/useEnterprise'
import type { Instance } from '../../../api/client'
import { isBusy } from '../../../lib/instance-status'
import { extractApiError } from '../shared'
import { Alert, Card, ConfirmDialog, LinkButton } from '../../../components/ui'
import { UploadProgress } from './bits'

// ── EnterpriseSection ─────────────────────────────────────────────────────────
//
// Bring-your-own Odoo Enterprise. The customer holds the licence and uploads
// their own tree; it is placed in this instance's own directory, mounted
// read-only, and never shared with another instance. The modules are put on
// addons_path only — the customer installs what they want from Odoo's own Apps
// screen. There is no sync/update affordance on purpose: an Enterprise bundle
// is replaced by uploading a new archive, never pulled.

export function EnterpriseSection({ instance }: { instance: Instance }) {
  const { t } = useTranslation()
  const fileRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [error, setError] = useState('')
  const [warning, setWarning] = useState('')
  const [confirmRemove, setConfirmRemove] = useState(false)

  const { data, isLoading } = useEnterpriseBundle(instance.id)
  const uploadMut = useUploadEnterprise(instance.id)
  const removeMut = useRemoveEnterprise(instance.id)
  const busy = isBusy(instance.status)
  const working = uploadMut.isPending || removeMut.isPending || busy

  const handleFile = async (file: File) => {
    setError(''); setWarning('')
    try {
      const res = await uploadMut.mutateAsync(file)
      if (res.warning) setWarning(res.warning)
    } catch (e) {
      setError(extractApiError(e) ?? t('detail.enterprise.uploadFailed', { defaultValue: 'Upload failed' }))
    }
  }

  // mutate + onError: the result is unused, so React Query keeps the rejection
  // in mutation state rather than handing back a promise nobody awaits.
  const handleRemove = () => {
    setError(''); setWarning('')
    removeMut.mutate(undefined, {
      onSuccess: () => setConfirmRemove(false),
      onError: (e) => setError(extractApiError(e) ?? t('detail.enterprise.removeFailed', { defaultValue: 'Removal failed' })),
    })
  }

  if (isLoading) return <p className="text-sm text-gray-500">{t('common.loading')}</p>

  const bundle = data?.bundle ?? null

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        {t('detail.enterprise.blurb', {
          defaultValue:
            'Upload your own Odoo Enterprise archive. It is mounted read-only into this instance only, and never shared. Modules are made available on the addons path - install the ones you need from Odoo’s Apps screen.',
        })}
      </p>

      {bundle && (
        <Card padded={false} className="enterprise-border p-4 space-y-2">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 font-medium text-gray-900 text-sm">
              <span aria-hidden className="enterprise-icon">◆</span>
              {bundle.filename}
            </span>
            <LinkButton
              tone="danger"
              onClick={() => setConfirmRemove(true)}
              disabled={working}
            >
              {t('detail.enterprise.remove', { defaultValue: 'Remove' })}
            </LinkButton>
          </div>
          <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <dt className="text-gray-400">{t('detail.enterprise.modules', { defaultValue: 'Modules' })}</dt>
              <dd className="text-gray-900 font-medium">{bundle.module_count}</dd>
            </div>
            <div>
              <dt className="text-gray-400">{t('detail.enterprise.size', { defaultValue: 'Size' })}</dt>
              <dd className="text-gray-900 font-medium">{formatBytes(bundle.size_bytes)}</dd>
            </div>
            <div>
              <dt className="text-gray-400">{t('detail.enterprise.uploadedBy', { defaultValue: 'Uploaded by' })}</dt>
              <dd className="text-gray-900 font-medium truncate" title={bundle.uploaded_by_email}>
                {bundle.uploaded_by_email || '-'}
              </dd>
            </div>
            <div>
              <dt className="text-gray-400">{t('detail.enterprise.uploadedAt', { defaultValue: 'Uploaded' })}</dt>
              <dd className="text-gray-900 font-medium">{new Date(bundle.uploaded_at).toLocaleString()}</dd>
            </div>
          </dl>
          <p className="text-[10px] text-gray-400 font-mono truncate" title={bundle.archive_sha}>
            sha256:{bundle.archive_sha.slice(0, 16)}
          </p>
        </Card>
      )}

      {/* Indigo, not brand: the Enterprise treatment is deliberately its own
          colour so it never reads as the gold OCA one. Left untouched. */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragOver(false)
          const f = e.dataTransfer.files[0]
          if (f) handleFile(f)
        }}
        onClick={() => { if (!working) fileRef.current?.click() }}
        className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
          working ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
        } ${dragOver ? 'border-indigo-400 bg-indigo-50' : 'border-gray-300 hover:border-gray-400'}`}
      >
        <p className="text-sm font-medium text-gray-700">
          {bundle
            ? t('detail.enterprise.dropReplace', { defaultValue: 'Drop a new archive to replace' })
            : t('detail.enterprise.drop', { defaultValue: 'Drop your Enterprise archive here' })}
        </p>
        <p className="text-xs text-gray-400 mt-1">
          {t('detail.enterprise.dropHint', { defaultValue: '.zip or .deb - your own Odoo Enterprise download' })}
        </p>
        <input ref={fileRef} type="file" accept=".zip,.deb" className="hidden" onChange={(e) => {
          const f = e.target.files?.[0]; if (f) handleFile(f)
        }} />
      </div>

      {uploadMut.isPending && (
        <UploadProgress
          progress={uploadMut.progress}
          uploadingLabel={t('detail.enterprise.uploadingPercent', { pct: uploadMut.progress, defaultValue: 'Uploading {{pct}}%' })}
          processingLabel={t('detail.enterprise.processing', { defaultValue: 'Unpacking and restarting Odoo…' })}
        />
      )}
      {removeMut.isPending && (
        <p className="text-xs text-gray-500">{t('detail.enterprise.removing', { defaultValue: 'Removing and restarting Odoo…' })}</p>
      )}
      {warning && <Alert tone="warning">{warning}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}

      {confirmRemove && (
        <ConfirmDialog
          title={t('detail.enterprise.removeTitle', { defaultValue: 'Remove Enterprise bundle?' })}
          message={t('detail.enterprise.removeConfirm', {
            defaultValue: 'The Enterprise tree is deleted and Odoo restarts as Community. Any installed Enterprise module will stop working.',
          })}
          confirmLabel={t('detail.enterprise.remove', { defaultValue: 'Remove' })}
          busy={removeMut.isPending}
          onConfirm={handleRemove}
          onCancel={() => setConfirmRemove(false)}
        />
      )}
    </div>
  )
}
